import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import "./QuestionnaireBuilder.css";

type QuestionId = string | number;
type Answers = Record<string, any>;
interface Question {
  id: QuestionId;
  type: string;
  text: string;
  required?: boolean;
  options?: Array<{ label: string; value: string }>;
}
interface QuestionnaireBuilderProps {
  name?: string;
  questions?: Question[];
  initialAnswers?: Answers;
  readOnly?: boolean;
  validateOnChange?: boolean;
  showSubmit?: boolean;
  submitLabel?: string;
  multiDisplay?: "select" | "checkbox";
  onChange?: (questionId: QuestionId, value: any) => void;
  onSubmit?: (answers: Answers) => void;
}
interface QuestionnaireBuilderRef {
  getAnswers: () => Answers;
  validate: () => boolean;
  reset: () => void;
}
const EMPTY_ANSWERS: Answers = {};
const isEmpty = (value: any) => value === null || value === undefined ||
  (typeof value === "string" && !value.trim()) || (Array.isArray(value) && value.length === 0);

const QuestionnaireBuilder = forwardRef<QuestionnaireBuilderRef, QuestionnaireBuilderProps>(
  ({ name, questions = [], initialAnswers = EMPTY_ANSWERS, readOnly = false,
    validateOnChange = true, showSubmit = false, submitLabel = "Enviar", multiDisplay = "select",
    onChange, onSubmit }, ref) => {
    const [answers, setAnswers] = useState<Answers>(initialAnswers);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const root = useRef<HTMLDivElement>(null);
    const prefix = useId();

    useEffect(() => { setAnswers(initialAnswers); }, [initialAnswers]);

    const handleAnswerChange = (questionId: QuestionId, value: any) => {
      if (readOnly) return;
      setAnswers((previous) => ({ ...previous, [questionId]: value }));
      onChange?.(questionId, value);
      if (validateOnChange || errors[questionId]) {
        const question = questions.find((item) => item.id === questionId);
        setErrors((previous) => ({ ...previous, [questionId]: question?.required && isEmpty(value) ? "Este campo es obligatorio" : "" }));
      }
    };
    const validate = () => {
      const nextErrors: Record<string, string> = {};
      questions.forEach((question) => {
        if (question.required && isEmpty(answers[question.id])) nextErrors[question.id] = "Este campo es obligatorio";
      });
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length) {
        requestAnimationFrame(() => {
          const invalid = root.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
          const focusable = invalid?.matches("input, textarea, button") ? invalid : invalid?.querySelector<HTMLElement>("input, button");
          focusable?.focus();
        });
        return false;
      }
      return true;
    };
    const reset = () => { setAnswers({}); setErrors({}); };
    useImperativeHandle(ref, () => ({ getAnswers: () => answers, validate, reset }));

    const renderQuestion = (question: Question, inputId: string) => {
      const value = answers[question.id];
      const common = {
        disabled: readOnly,
        className: errors[question.id] ? "p-invalid" : "",
        "aria-invalid": !!errors[question.id],
        "aria-describedby": errors[question.id] ? inputId + "-error" : undefined,
        "aria-required": !!question.required,
      };
      if (question.type === "number") return (
        <InputNumber {...common} inputId={inputId} value={value ?? null} useGrouping={false}
          onValueChange={(event) => handleAnswerChange(question.id, event.value)} placeholder="Ingresa un número" />
      );
      if (question.type === "single" || question.type === "checkbox" || (question.type === "multi" && multiDisplay === "checkbox")) {
        const multiple = question.type !== "single";
        return (
          <div {...common} id={inputId} role={multiple ? "group" : "radiogroup"} aria-labelledby={inputId + "-label"} className="qb-choice-list">
            {(question.options || []).map((option, index) => (
              <div className="qb-choice" key={option.value}>
                {multiple ? <Checkbox inputId={inputId + "-" + index}
                  checked={Array.isArray(value) && value.includes(option.value)} disabled={readOnly}
                  onChange={(event) => {
                    const current = Array.isArray(value) ? value : [];
                    handleAnswerChange(question.id, event.checked ? [...current, option.value] : current.filter((item) => item !== option.value));
                  }} /> : <RadioButton inputId={inputId + "-" + index} name={inputId} value={option.value}
                  checked={value === option.value} disabled={readOnly}
                  onChange={(event) => handleAnswerChange(question.id, event.value)} />}
                <label htmlFor={inputId + "-" + index}>{option.label}</label>
              </div>
            ))}
          </div>
        );
      }
      if (question.type === "multi") return (
        <MultiSelect {...common} inputId={inputId} value={value || []} options={question.options || []}
          onChange={(event) => handleAnswerChange(question.id, event.value)} placeholder="Selecciona opciones"
          panelClassName="teamassess-form-menu" />
      );
      if (question.type === "textarea") return (
        <InputTextarea {...common} id={inputId} rows={4} value={value ?? ""}
          onChange={(event) => handleAnswerChange(question.id, event.target.value)} placeholder="Ingresa tu respuesta" />
      );
      return <InputText {...common} id={inputId} value={value ?? ""}
        onChange={(event) => handleAnswerChange(question.id, event.target.value)} placeholder="Ingresa tu respuesta" />;
    };

    return (
      <div className="questionnaire-builder teamassess-form" ref={root}>
        {name && <h3 className="qb-title">{name}</h3>}
        <div className="qb-questions">
          {questions.map((question) => {
            const inputId = prefix + "-" + question.id;
            return (
              <div key={question.id} className="question-item">
                <label id={inputId + "-label"} htmlFor={inputId} className="qb-label">
                  {question.text}{question.required && <span className="ta-required" aria-hidden="true"> *</span>}
                </label>
                {renderQuestion(question, inputId)}
                {errors[question.id] && <small id={inputId + "-error"} className="ta-field-error">{errors[question.id]}</small>}
              </div>
            );
          })}
        </div>
        {showSubmit && <div className="qb-actions">
          <Button type="button" label={submitLabel} disabled={readOnly} onClick={() => { if (validate()) onSubmit?.(answers); }} />
        </div>}
      </div>
    );
  }
);
QuestionnaireBuilder.displayName = "QuestionnaireBuilder";
export default QuestionnaireBuilder;



